import React, { useEffect, useState } from "react";
import { Button, Checkbox, TextField, TextareaAutosize } from "@mui/material";
import MuiTable from "../../../../common-utilities/core/mui-table";
import CustomModal from "../../../../common-utilities/core/modal";
import { setProperty } from "../../../../common-utilities/utils/misc";
import { FINANCE_V2_HOSTNAME } from "../../../../common-utilities/utils/end-points";
import { FINANCE_REST_URLS } from "../../../../common-utilities/utils/end-points/finance-v2";
import { HTTP_METHODS, invokeApi } from "../../../utils/http-service";
import { toast } from "react-toastify";
import ReconsiderAction from "../../reconsider-action";
import { formatDate } from "../../../../common-utilities/utils";
import { DATE_FORMATS } from "../../../../common-utilities/utils/constants";
import { notifyBulkActionResult } from "../../../utils/utils";
import "./index.scss";

const VendorMappingApprovalList = ({
  approvalType = "vendor_mapping",
  status = "approval_in_progress",
  searchPlaceHolder = "Search by Vendor Name / Code",
  getPendingVendorMapping
}) => {
  const [approvalList, setApprovalList] = useState([]);
  const [filters, setFilters] = useState({
    approvalType,
    status,
    sortBy: "-createdAt",
    page: 1,
  });
  const [rejectDetails, setRejectDetails] = useState(null);
  const [comment, setComment] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);

  useEffect(() => {
    loadData(filters);
  }, []);

  const loadData = (providedFilters = filters) => {
    const params = {
      ...providedFilters,
      approvalType,
      status,
    };
    delete params.page;
    invokeApi(
      HTTP_METHODS.POST,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_MY_APPROVALS}`,
      null,
      params
    )
      .then((response) => {
        if (response?.message) {
          toast.error(response.message, { autoClose: 2000 });
          return;
        }
        setApprovalList(response || []);
       
      })
      .catch((err) => {
        toast.error(err || "Failed to load vendor mapping approvals", {
          autoClose: 2000,
        });
      });
  };

  const onApprove = (approval) => {
    invokeApi(
      HTTP_METHODS.POST,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.APPROVE}/${approval.id}`
    )
      .then((response) => {
        if (response?.code || response?.message) {
          toast.error(response?.message || "Failed to approve", {
            autoClose: 2000,
          });
          return;
        }
        toast.success("Approved successfully", { autoClose: 2000 });
        loadData(filters);
        getPendingVendorMapping()
      })
      .catch((err) => {
        toast.error(err || "Failed to approve", { autoClose: 2000 });
      });
  };

  const handleRowSelection = (event, data, type) => {
    const isChecked = event.target.checked;
    if (type === "selectAll") {
      setSelectedRows(isChecked ? [...approvalList] : []);
    } else {
      let updatedSelectedRows = [...selectedRows];
      if (isChecked) {
        updatedSelectedRows.push(data);
      } else {
        updatedSelectedRows = updatedSelectedRows.filter(
          (ele) => ele.id !== data.id
        );
      }
      setSelectedRows(updatedSelectedRows);
    }
  };

  const bulkApprove = () => {
    if (!selectedRows.length) {
      return;
    }
    const approvalIds = selectedRows.map((ele) => parseInt(ele.id));
    invokeApi(
      HTTP_METHODS.POST,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.BULK_APPROVE}`,
      { approvalIds }
    )
      .then((res) => {
        if (res?.message) {
          toast.error(res.message, { autoClose: 2000 });
          return;
        }
        setSelectedRows([]);
        setIsBulkModalOpen(false);
        loadData(filters);
        getPendingVendorMapping();
        notifyBulkActionResult(res, "Approved");
      })
      .catch((err) => {
        toast.error(err || "Failed to approve selected records", {
          autoClose: 2000,
        });
      });
  };

  const bulkReconsider = () => {
    if (!selectedRows.length) {
      return;
    }
    const approvalIds = selectedRows.map((ele) => parseInt(ele.id));
    invokeApi(
      HTTP_METHODS.POST,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.BULK_RECONSIDER}`,
      { approvalIds }
    )
      .then((res) => {
        if (res?.message) {
          toast.error(res.message, { autoClose: 2000 });
          return;
        }
        setSelectedRows([]);
        setIsBulkModalOpen(false);
        loadData(filters);
        getPendingVendorMapping();
        notifyBulkActionResult(res, "Reconsidered");
      })
      .catch((err) => {
        toast.error(err || "Failed to reconsider selected records", {
          autoClose: 2000,
        });
      });
  };

  const bulkReject = () => {
    if (!selectedRows.length) {
      return;
    }
    const approvalIds = selectedRows.map((ele) => parseInt(ele.id));
    invokeApi(
      HTTP_METHODS.POST,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.BULK_REJECT}`,
      { approvalIds }
    )
      .then((res) => {
        if (res?.message) {
          toast.error(res.message, { autoClose: 2000 });
          return;
        }
        setSelectedRows([]);
        setIsBulkModalOpen(false);
        loadData(filters);
        getPendingVendorMapping();
        notifyBulkActionResult(res, "Rejected");
      })
      .catch((err) => {
        toast.error(err || "Failed to reject selected records", {
          autoClose: 2000,
        });
      });
  };

  const onReject = () => {
    if (!comment?.trim()) {
      toast.error("Comment is required", { autoClose: 2000 });
      return;
    }
    invokeApi(
      HTTP_METHODS.POST,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.REJECT}`,
      null,
      {
        id: rejectDetails?.id,
        comment: comment.trim(),
      }
    )
      .then((response) => {
        if (response?.code || response?.message) {
          toast.error(response?.message || "Failed to reject", {
            autoClose: 2000,
          });
          return;
        }
        toast.success("Rejected successfully", { autoClose: 2000 });
        setRejectDetails(null);
        setComment("");
        loadData(filters);
        getPendingVendorMapping()
      })
      .catch((err) => {
        toast.error(err || "Failed to reject", { autoClose: 2000 });
      });
  };

  const getHeaderConfig = () => [
    {
      id: "checkbox",
      label: (
        <Checkbox
          onChange={(event) => handleRowSelection(event, null, "selectAll")}
          checked={
            selectedRows.length === approvalList.length &&
            approvalList.length !== 0
          }
        />
      ),
      sortable: false,
      render: (data) => (
        <Checkbox
          checked={
            selectedRows.find((ele) => ele.id === data.id) ? true : false
          }
          onChange={(event) => handleRowSelection(event, data)}
        />
      ),
    },
    {
      label: "Vendor Name",
      id: "document.name",
      render: (data) => data?.document?.name || data?.name || "-",
    },
    {
      label: "Vendor Code",
      id: "document.vendorCode",
      render: (data) => data?.document?.vendorCode || data?.vendorCode || "-",
    },
    {
      label: "Item Name",
      id: "document.item.name",
      render: (data) =>
        data?.document?.itemName ||"-",
    },
    {
      label: "Item Code",
      id: "document.item.itemCode",
      render: (data) =>
        data?.document?.itemCode
        ||"-",
    },
    {
      label: "Price",
      id: "document.price",
      render: (data) => data?.document?.price || data?.price || "-",
    },
    {
      label: "Tax Percentage",
      id: "document.taxPercentage",
      render: (data) => data?.document?.taxPercentage || data?.taxPercentage || "-",
    },
    {
      label: "Valid From",
      id: "document.fromDate",
      render: (data) =>
        formatDate(data?.document?.fromDate || data?.fromDate, DATE_FORMATS["DD-MM-YYYY"]),
    },
    {
      label: "Valid To",
      id: "document.toDate",
      render: (data) =>
        formatDate(data?.document?.toDate || data?.toDate, DATE_FORMATS["DD-MM-YYYY"]),
    },
    {
      label: "Actions",
      id: "actions",
      render: (data) => (
        <>
          <Button variant="text" onClick={() => onApprove(data)}>
            Approve
          </Button>
          <ReconsiderAction
            approvalDetails={data}
            title="Reconsider Vendor Mapping"
            buttonProps={{ variant: "text" }}
            onSuccess={() => {
              loadData(filters);
              getPendingVendorMapping();
            }}
          />
          <Button
            variant="text"
            color="error"
            onClick={() => {
              setRejectDetails(data);
              setComment("");
            }}
          >
            Reject
          </Button>
        </>
      ),
    },
  ];

  return (
    <div className="finance-v2-vm-list">
      <div className="vm-header-section">
        <div className="left-container">
          <TextField
            placeholder={searchPlaceHolder}
            value={filters?.searchKey || ""}
            size="small"
            onChange={(e) => setProperty("searchKey", e.target.value, setFilters)}
          />
          <Button
            variant="outlined"
            onClick={() => {
              const updatedFilters = { ...filters, page: 1 };
              setFilters(updatedFilters);
              loadData(updatedFilters);
            }}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            disabled={!selectedRows.length}
            onClick={() => setIsBulkModalOpen(true)}
          >
            Bulk Approve{selectedRows.length ? ` (${selectedRows.length})` : ""}
          </Button>
        </div>
      </div>
      <MuiTable
        columnsList={getHeaderConfig()}
        dataList={
          approvalList.slice(
            ((filters.page || 1) - 1) * 10,
            ((filters.page || 1) - 1) * 10 + 10
          ) || []
        }
        filters={filters}
        pageCount={Math.ceil((approvalList?.length || 0) / 10)}
        onChange={(page) => {
          setFilters({
            ...filters,
            page,
          });
        }}
      />
      {isBulkModalOpen && (
        <CustomModal
          title="Bulk Approve/Reject/Reconsider"
          onClose={() => setIsBulkModalOpen(false)}
        >
          <div className="approve-btn-con finance-bulk-btn">
            <Button variant="outlined" sx={{ mr: 2 }} onClick={bulkApprove}>
              Approve
            </Button>
            <Button
              variant="outlined"
              sx={{ mr: 2 }}
              onClick={bulkReconsider}
              color="warning"
            >
              Reconsider
            </Button>
            <Button variant="outlined" color="error" onClick={bulkReject}>
              Reject
            </Button>
          </div>
        </CustomModal>
      )}
      {rejectDetails && (
        <CustomModal
          title="Reject Vendor Mapping"
          onClose={() => {
            setRejectDetails(null);
            setComment("");
          }}
        >
          <TextareaAutosize
            placeholder="Comment"
            minRows={6}
            value={comment}
            style={{ width: "100%" }}
            onChange={(e) => setComment(e.target.value)}
          />
          <div className="approve-btn-con finance-bulk-btn">
            <Button variant="outlined" sx={{ mr: 2 }} onClick={onReject} color="error">
              Confirm Reject
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setRejectDetails(null);
                setComment("");
              }}
            >
              Cancel
            </Button>
          </div>
        </CustomModal>
      )}
    </div>
  );
};

export default VendorMappingApprovalList;
